import React from 'react';
import { Linkedin, MapPin, Mail, Instagram, MessageCircle } from "lucide-react";

const Footer2 = ({
  logo = {
    src: "/dmls-logo.png",
    alt: "DMLS Logo",
    title: "DMLS",
    url: "#",
  },
  tagline = "Building Machine Learning Community at Dalhousie University",
  menuItems = [
    {
      title: "Society",
      links: [
        { text: "About Us", url: "/" },
        { text: "Our Team", url: "/team" },
        { text: "Events", url: "/events" },
        { text: "Join Us", url: "https://airtable.com/appbzvXorTGCwSsy9/pagrrVOHqpocEY5IX/form" },
      ],
    },
    {
      title: "Resources",
      links: [
        { text: "Article of the Week", url: "/article-of-the-week" },
      ],
    },
    {
      title: "Community",
      links: [
        { text: "Discord", url: "https://discord.com/invite/47Pyqyhnuu?fbclid=PAZXh0bgNhZW0CMTEAAaf3CY6keaV8UQoHI_ZnlsAlax2Ea_2Sz1LFNBZN6arK2OVmRWSanx7MTnYH_A_aem_fCfe5I9D6Z0mA2c8PfV6cg" },
      ],
    },
    {
      title: "Connect",
      links: [
        { text: "Contact Us", url: "https://airtable.com/appbzvXorTGCwSsy9/pagj6WhNYHu378lqJ/form" },
        { text: "Partnerships", url: "mailto:dmls@dal.ca?subject=Partnership%20Inquiry" },
        { text: "Sponsorship", url: "mailto:dmls@dal.ca?subject=Sponsorship%20Inquiry" },
      ],
    },
  ],
  copyright = "© 2026 Dalhousie Machine Learning Society. All rights reserved.",
  bottomLinks = [],
}) => {
  return (
    <section className="bg-black text-white py-16">
      <div className="container mx-auto px-4 max-w-7xl">
        <footer>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-16">
            {/* Logo and Description */}
            <div className="col-span-1 md:col-span-2 lg:col-span-2 space-y-6">
              <div className="flex items-center gap-6">
                <img
                  src="/DMLSLogo.png"
                  alt="DMLS Logo"
                  className="w-12 h-12 border-2 border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,1)]"
                />
                <div>
                  <h3 className="text-2xl font-black font-[var(--font-anton)] text-white">
                    DMLS
                  </h3>
                  <div className="h-1 w-16 bg-[#0395fc] transform -skew-x-12"></div>
                </div>
              </div>
              
              <p className="text-gray-300 font-[var(--font-dm-sans)] text-lg leading-relaxed max-w-md text-left">
                {tagline}
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <MapPin className="w-5 h-5 text-[#0395fc]" />
                  <span className="font-[var(--font-dm-sans)]">Dalhousie University, Halifax, NS</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Mail className="w-5 h-5 text-[#0395fc]" />
                  <span className="font-[var(--font-dm-sans)]">dmls@dal.ca</span>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="flex gap-4 pt-4 pb-8">
                <a href="https://www.linkedin.com/company/dalmlsociety/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white border-2 border-white shadow-[3px_3px_0px_0px_rgba(3,149,252,1)] hover:shadow-[1px_1px_0px_0px_rgba(3,149,252,1)] hover:translate-x-1 hover:translate-y-1 transition-all duration-200 flex items-center justify-center">
                  <Linkedin className="w-5 h-5 text-black" />
                </a>
                <a href="https://www.instagram.com/dalmlsociety/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white border-2 border-white shadow-[3px_3px_0px_0px_rgba(3,149,252,1)] hover:shadow-[1px_1px_0px_0px_rgba(3,149,252,1)] hover:translate-x-1 hover:translate-y-1 transition-all duration-200 flex items-center justify-center">
                  <Instagram className="w-5 h-5 text-black" />
                </a>
                <a href="https://discord.com/invite/47Pyqyhnuu?fbclid=PAZXh0bgNhZW0CMTEAAaf3CY6keaV8UQoHI_ZnlsAlax2Ea_2Sz1LFNBZN6arK2OVmRWSanx7MTnYH_A_aem_fCfe5I9D6Z0mA2c8PfV6cg" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white border-2 border-white shadow-[3px_3px_0px_0px_rgba(3,149,252,1)] hover:shadow-[1px_1px_0px_0px_rgba(3,149,252,1)] hover:translate-x-1 hover:translate-y-1 transition-all duration-200 flex items-center justify-center md:hidden">
                  <MessageCircle className="w-5 h-5 text-black" />
                </a>
              </div>
            </div>
            
            {/* Menu Items */}
            {menuItems.map((section, sectionIdx) => (
              <div key={sectionIdx} className={`space-y-4 ${section.title === 'Community' ? 'hidden md:block' : ''}`}>
                <h3 className="text-lg font-black font-[var(--font-anton)] text-[#0395fc] uppercase tracking-wider">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link, linkIdx) => {
                    // Hide Partnerships and Sponsorship on mobile for Connect section
                    const shouldHideOnMobile = section.title === 'Connect' && (link.text === 'Partnerships' || link.text === 'Sponsorship');
                    
                    return (
                      <li key={linkIdx} className={shouldHideOnMobile ? 'hidden md:block' : ''}>
                        <a 
                          href={link.url}
                          target={link.text === "Contact Us" ? "_blank" : "_self"}
                          rel={link.text === "Contact Us" ? "noopener noreferrer" : ""}
                          className="text-gray-300 hover:text-[#0395fc] font-[var(--font-dm-sans)] transition-colors duration-200 hover:underline"
                        >
                          {link.text}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          
          {/* Bottom Section */}
          <div className="border-t-2 border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 font-[var(--font-dm-sans)] text-sm">
              {copyright}
            </p>
            <ul className="flex gap-6">
              {bottomLinks.map((link, linkIdx) => (
                <li key={linkIdx}>
                  <a 
                    href={link.url}
                    className="text-gray-400 hover:text-[#0395fc] font-[var(--font-dm-sans)] text-sm transition-colors duration-200 hover:underline"
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </footer>
      </div>
    </section>
  );
};

export { Footer2 };